import React from 'react';
import {FlatList, StyleSheet, View} from 'react-native';
import {NavigationContainer, NavigationIndependentTree} from '@react-navigation/native';
import {ThemeProvider} from '@rneui/themed';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import rewardsTheme from '../../../theme/rewardsTheme';
import {OffersItemWithMflId} from '../../../transport/types';
import Text from '../../themed/Text';

import ExclusiveOffersTileCompact from './ExclusiveOffersTileCompact';
import ExclusiveOffersTileLarge from './ExclusiveOffersTileLarge';

const offerHeadlines = [
  'Save $10',
  'Buy 2 Get 1 Free',
  '20% Off Spirits',
  '$5 Off Wine',
  'Half Price Beer',
  'Member Exclusive',
  'Weekend Special',
  'Flash Sale',
  'Limited Time',
  'Bonus Points',
  'Double Rewards',
  'Free Delivery',
  'Bundle Deal',
  'Clearance Sale',
  'VIP Offer',
  'Early Access',
  'Staff Pick',
  'Best Seller',
  'New Arrival',
  'Seasonal Special',
];

const offerDescriptions = [
  'Get $10 off when you spend $50 or more on selected wines.',
  'Purchase any two bottles and receive a third absolutely free.',
  'All premium spirits reduced for members this weekend.',
  'Save $5 on any bottle of wine over $20.',
  'Half price on selected craft beers while stocks last.',
  'Exclusive deal for loyalty members only.',
  'Special pricing this weekend only.',
  'Limited time flash sale on selected items.',
  "Offer ends soon, don't miss out.",
  'Earn bonus points on every purchase.',
  'Double your rewards points today.',
  'Free delivery on orders over $100.',
  'Save when you buy the bundle.',
  'Clearance prices on last season stock.',
  'VIP members get early access.',
  'Be the first to try new products.',
  'Handpicked by our expert staff.',
  "Our customers' top choice.",
  'Just arrived in store.',
  'Perfect for the season.',
];

const generateMockOffers = (count: number): OffersItemWithMflId[] => {
  return Array.from({length: count}, (_, i) => ({
    mflId: `offer-${i + 1}`,
    offerId: `LM${String(i + 1).padStart(3, '0')}`,
    heroImage: [
      {
        id: `hero-${i + 1}`,
        url: `https://picsum.photos/seed/offer${i + 1}/600/390`,
        filename: 'hero.jpg',
        size: 50000,
        type: 'image',
        width: 600,
        height: 390,
      },
    ],
    heroImageDesktop: [
      {
        id: `hero-desktop-${i + 1}`,
        url: `https://picsum.photos/seed/offer${i + 1}desktop/800/520`,
        filename: 'hero-desktop.jpg',
        size: 80000,
        type: 'image',
        width: 800,
        height: 520,
      },
    ],
    tileHeadline: offerHeadlines[i % offerHeadlines.length],
    headline: offerHeadlines[i % offerHeadlines.length],
    description: offerDescriptions[i % offerDescriptions.length],
    tenant: ['BMT'],
    products: [`product-${i + 1}`],
    barcodes: [`${1234567890 + i}`],
    status: 'Approved - Live',
    offerTrigger: undefined,
  }));
};

type CarouselVariant = 'cart' | 'rewards';

interface ExclusiveOffersCarouselProps {
  offers: OffersItemWithMflId[];
  variant: CarouselVariant;
}

function Separator() {
  return <View style={{width: 8, height: 8}} />;
}

/**
 * Pure component that mimics ExclusiveOffers carousel without MobX dependencies.
 * Matches the FlatList configuration from index.tsx exactly.
 */
function ExclusiveOffersCarousel({offers, variant}: ExclusiveOffersCarouselProps) {
  const renderItem = ({item}: {item: OffersItemWithMflId}) => {
    return variant === 'cart' ? (
      <ExclusiveOffersTileCompact {...item} />
    ) : (
      <ExclusiveOffersTileLarge {...item} />
    );
  };

  const flatlistStyle = variant === 'cart' ? styles.flatlistCart : undefined;

  return (
    <View>
      {variant === 'cart' && (
        <Text style={styles.cartHeaderText} color="black">
          Choose one of the offers below
        </Text>
      )}
      <FlatList
        style={flatlistStyle}
        horizontal
        data={offers}
        renderItem={renderItem}
        keyExtractor={item => item.offerId}
        ItemSeparatorComponent={Separator}
        showsHorizontalScrollIndicator={true}
        windowSize={3}
        maxToRenderPerBatch={3}
        initialNumToRender={3}
        removeClippedSubviews={true}
      />
    </View>
  );
}

const meta = {
  title: 'Components/Rewards/Exclusive Offers Carousel',
  component: ExclusiveOffersCarousel,
  decorators: [
    Story => (
      <NavigationIndependentTree>
        <NavigationContainer>
          <ThemeProvider theme={rewardsTheme}>
            <View style={styles.container}>
              <Story />
            </View>
          </ThemeProvider>
        </NavigationContainer>
      </NavigationIndependentTree>
    ),
  ],
} satisfies Meta<typeof ExclusiveOffersCarousel>;

export default meta;

type Story = StoryObj<typeof meta>;

// Cart variant (Compact tiles)
export const CartWith5_Offers: Story = {
  args: {
    offers: generateMockOffers(5),
    variant: 'cart',
  },
};

export const CartWith10_Offers: Story = {
  args: {
    offers: generateMockOffers(10),
    variant: 'cart',
  },
};

export const CartWith20_Offers: Story = {
  args: {
    offers: generateMockOffers(20),
    variant: 'cart',
  },
};

// Rewards variant (Large tiles)
export const RewardsWith5_Offers: Story = {
  args: {
    offers: generateMockOffers(5),
    variant: 'rewards',
  },
};

export const RewardsWith10_Offers: Story = {
  args: {
    offers: generateMockOffers(10),
    variant: 'rewards',
  },
};

export const RewardsWith20_Offers: Story = {
  args: {
    offers: generateMockOffers(20),
    variant: 'rewards',
  },
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  flatlistCart: {
    marginBottom: -16,
  },
  cartHeaderText: {
    marginBottom: 16,
  },
});
